import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useSWR from 'swr'
import { 
  Stack, 
  HStack, 
  VStack,
  Center, 
  Button, 
  Text,
  Tag,
  TagLabel,
  TagLeftIcon,
  useToast 
} from '@chakra-ui/react'
import { AiOutlineCheck, AiFillThunderbolt } from 'react-icons/ai';
import { getGameReviews, postReviewAbuse } from "src/api";
import GameCommentCard, { CommentSkeleton } from './GameCommentCard';
import { useUser } from 'src/context';
import { getCurrentTimeString, trackEvent } from 'utils';
import { getOneReview } from "src/api";

const GameCommentBlock = props => {
  const { gameId, title, isEditingReview, onStartToReview } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [reviewDataList, setReviewDataList] = useState([]);
  const [lastReviewCustomId, setLastReviewCustomId] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [selectedSort, setSelectedSort] = useState("lastModifiedTime"); // lastModifiedTime, playDate, feedbackPoints, ratingAsc
  const [isSpoilerOff, setIsSpoilerOff] = useState(false);

  const { isLogin, user } = useUser();
  const { email, reviewedGamesIdList, photoUrl, displayName, title: userTitle, titleText: userTitleText } = user;

  const toast = useToast();

  const hasReviewed = useMemo(() => {
    return reviewedGamesIdList.indexOf(gameId) > -1;
  }, [gameId, reviewedGamesIdList]);


  const { 
    data: reviewRes,
    error, 
    isLoading: isFetchingOwnReview 
  } = useSWR(hasReviewed ? gameId : null, getOneReview);
  // will auto revalidate when review created or updated

  const ownReviewContent = reviewRes?.data || null;

  const isShowOwnReview = useMemo(() => {
    return !isFetchingOwnReview && hasReviewed && !!ownReviewContent.rating;
  }, [isFetchingOwnReview, isEditingReview, hasReviewed, ownReviewContent]);

  useEffect(() => {
    handleLoad([], "");

    return () => {
      setReviewDataList([]);
      setLastReviewCustomId("");
      setHasMore(true);
    }
  }, [gameId]);

  const handleReport = useCallback(async ({ customId, userName, reason, comment }) => {
    const currentTime = new Date();

    const postContent = {
      gameId,
      title,
      customId,
      userName,
      comment,
      reason,
      reporterEmail: isLogin ? email : "",
      reportTime: getCurrentTimeString(currentTime),
    }

    try {
      await postReviewAbuse(postContent);
    } catch (err) {
      console.log(err);
    } finally {
      toast({
        title: "感謝您的回報",
        description: "我們會盡快確認此則評論內容並作適當處理。",
        status: 'success',
        position: 'top',
        duration: 3000,
        isClosable: true,
      })
    }
  }, [gameId, isLogin])

  const handleLoad = async (currentDataList = [], lastReviewCustomId = "", sort = "") => {
    const query = {
      gameId,
      lastUserCustomId: lastReviewCustomId,
      sort
    }
  
    if (lastReviewCustomId) {
      trackEvent({
        category: 'GameReview_loadmore',
        action: 'Click',
        label: `${gameId}-${title}`,
      });
    };

    setIsLoading(true);

    try {
      const res = await getGameReviews(query);

      const { reviewDataList: newReviewDataList, lastUserCustomId, hasMore } = res.data;
  
      if (newReviewDataList && newReviewDataList.length > 0) {
        setReviewDataList([...currentDataList, ...newReviewDataList])
        setLastReviewCustomId(lastUserCustomId)
      }

      setHasMore(hasMore);
    } catch (err) {
      console.log(err)
      Sentry.captureException(err);
    } finally {
      setIsLoading(false);
    }
  }

  const handleSortChange = (sort) => {
    if (sort === selectedSort) return;

    setSelectedSort(sort);

    handleLoad([], "", sort);
  }

  const toggleSpoiler = () => {
    setIsSpoilerOff(prev => !prev)
  }

  if (isFetchingOwnReview || (isLoading && reviewDataList.length === 0)) {
    return (
      <Stack spacing={2}>
        <CommentSkeleton />
        <CommentSkeleton />
      </Stack>
    )
  }

  return (
    <Stack spacing={2}>
      <HStack justify='space-between' flexWrap='wrap'>
        <HStack spacing={3}>
          <Tag 
            borderRadius='full'
            variant={selectedSort === 'lastModifiedTime' ? 'solid' : 'outline'}
            colorScheme='blue'
            size='md'
            _hover={{ cursor: selectedSort === 'lastModifiedTime' ? 'default' : 'pointer' }}
            onClick={() => handleSortChange("lastModifiedTime")}
          >
            {selectedSort === 'lastModifiedTime' && <TagLeftIcon boxSize={4} as={AiOutlineCheck} />}
            <TagLabel>最新發表</TagLabel>
          </Tag>

          <Tag 
            borderRadius='full'
            variant={selectedSort === 'playDate' ? 'solid' : 'outline'}
            colorScheme='blue'
            _hover={{ cursor: selectedSort === 'playDate' ? 'default' : 'pointer' }}
            onClick={() => handleSortChange("playDate")}
          >
            {selectedSort === 'playDate' && <TagLeftIcon boxSize={4} as={AiOutlineCheck} />}
            <TagLabel>最近遊玩</TagLabel>
          </Tag>

          <Tag 
            borderRadius='full'
            variant={selectedSort === 'feedbackPoints' ? 'solid' : 'outline'}
            colorScheme='blue'
            _hover={{ cursor: selectedSort === 'feedbackPoints' ? 'default' : 'pointer' }}
            onClick={() => handleSortChange("feedbackPoints")}
          >
            {selectedSort === 'feedbackPoints' && <TagLeftIcon boxSize={4} as={AiOutlineCheck} />}
            <TagLabel>最有幫助</TagLabel>
          </Tag>

          <Tag 
            borderRadius='full'
            variant={selectedSort === 'ratingAsc' ? 'solid' : 'outline'}
            colorScheme='blue'
            _hover={{ cursor: selectedSort === 'ratingAsc' ? 'default' : 'pointer' }}
            onClick={() => handleSortChange("ratingAsc")}
          >
            {selectedSort === 'ratingAsc' && <TagLeftIcon boxSize={4} as={AiOutlineCheck} />}
            <TagLabel>評分最低</TagLabel>
          </Tag>
        </HStack>

        <HStack flexGrow='1' justify='flex-end'>
          <Tag 
            borderRadius='full'
            variant='solid'
            colorScheme={isSpoilerOff ? 'red' : 'green'}
            _hover={{ cursor: 'pointer' }}
            onClick={toggleSpoiler}
          >
            <TagLeftIcon boxSize={4} as={AiFillThunderbolt} />
            <TagLabel>目前{isSpoilerOff ? '顯示' : '隱藏'}劇透</TagLabel>
          </Tag>
        </HStack>
      </HStack>

      <Stack spacing={2}>
        {isShowOwnReview && (
          <GameCommentCard 
            gameId={gameId} 
            title={title}
            userName={displayName}
            userPhotoUrl={photoUrl}
            userTitle={userTitle}
            userTitleText={userTitleText}
            isPublicUserPage={false}
            isOwn={true}
            onEdit={onStartToReview}
            {...ownReviewContent}
          />
        )}

        {reviewDataList.length > 0 
          ? reviewDataList.map((review, idx) => {
            const { isHidden, isSpoiler, customId } = review;

            if (isHidden || (isSpoiler && !isSpoilerOff) || customId === user.customId) return;

            return (
              <GameCommentCard 
                key={`review-${customId}-${idx}`}
                gameId={gameId} 
                title={title}
                onReport={handleReport}
                {...review}
              />
            )
          })
          : (
            <VStack>
              <Text align='center'>還沒有足夠數量的評價可以參考</Text>
              {!hasReviewed && (
                <Button 
                  variant="ghost" 
                  colorScheme="secondary"
                  onClick={onStartToReview}
                >
                  分享你的心得！
                </Button>
              )}
            </VStack>
          )
        }

        {isLoading && reviewDataList.length > 0 &&
          <>
            <CommentSkeleton />
            <CommentSkeleton />
          </>
        }
      </Stack>

      {reviewDataList.length > 0 &&
        <Center> 
          {hasMore ? (
            <Button 
              variant='ghost' 
              onClick={() => handleLoad(reviewDataList, lastReviewCustomId, selectedSort)} 
              disabled={isLoading}
            >
              查看更多評價
            </Button>
          ) : (
            <Text align='center'>沒有更多評價囉！</Text>
          )} 
        </Center>
      }
    </Stack>
  )
};

export default GameCommentBlock;